import React, { useState } from "react";
import {
  Box,
  Button,
  Badge,
  Menu,
  List,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Paper,
  Fade,
  IconButton,
  Tooltip,
} from "@mui/material";
import MailIcon from "@mui/icons-material/Mail";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../../../hooks/notifications/useNotifications";
import { useAuth } from "hooks/auth/useAuth";
import { NotificationItem } from  "@/components/features/notifications/NotificationItem";
import { formatNotificationDate } from "utils";

const NotificationAccordion: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { notifications, markAsRead, markAllAsRead } = useNotifications(
    userInfo?.id || ""
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    const dateA = new Date(a.createdAt || "");
    const dateB = new Date(b.createdAt || "");
    return dateB.getTime() - dateA.getTime();
  });

  const unreadNotifications = sortedNotifications.filter((n) => !n.read);
  const recentNotifications = sortedNotifications.slice(0, 8);

  const handleMarkAsRead = (
    event: React.MouseEvent,
    notificationId?: string
  ) => {
    event.stopPropagation();
    if (notificationId) {
      markAsRead.mutate(notificationId);
    } else {
      console.error("No notification ID provided");
    }
  };

  return (
    <Box>
      <Tooltip title="Notifications" placement="bottom">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            color: "inherit",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Badge
            badgeContent={unreadNotifications.length}
            color="error"
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <svg width="0" height="0">
                <linearGradient
                  id="mailIconGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="20%" stopColor="#0062ff" />
                  <stop offset="30%" stopColor="#00a6ff" />
                  <stop offset="100%" stopColor="#e6f3ff" />
                </linearGradient>
              </svg>
              <MailIcon
                sx={{
                  fill: "url(#mailIconGradient)",
                  fontSize: 24,
                }}
              />
            </Box>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 8,
          sx: {
            overflow: "visible",
            mt: 1,
            width: { xs: "min(90vw, 380px)", sm: "420px" },
            maxWidth: { xs: "min(90vw, 380px)", sm: "420px" },
            maxHeight: { xs: "80vh", sm: "600px" },
            borderRadius: 2,
            "& > *": {
              overflow: "hidden",
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 12,
              height: 12,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        slotProps={{
          paper: {
            sx: {
              position: { xs: "fixed", sm: "absolute" },
              top: { xs: "64px", sm: "auto" },
              left: { xs: "50%", sm: "unset" },
              right: { xs: "unset", sm: "unset" },
              transform: {
                xs: "translateX(-50%) !important",
                sm: "unset !important",
              },
            },
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 0,
            bgcolor: "transparent",
            maxHeight: { xs: "80vh", sm: "600px" },
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px 12px 0 0",
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
                  Notifications
                </Typography>
                {unreadNotifications.length > 0 && (
                  <Chip
                    size="small"
                    label={unreadNotifications.length}
                    color="primary"
                    sx={{ 
                      height: 22, 
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              {unreadNotifications.length > 0 && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsRead.mutate();
                  }}
                  disabled={markAllAsRead.isPending}
                  sx={{ 
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    textTransform: "none",
                  }}
                >
                  Mark all read
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, width: "100%" }}>
            <List sx={{ py: 1, width: "100%", px: 0 }}>
              {recentNotifications.length > 0 ? (
                recentNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onNavigate={navigate}
                    formatDate={formatNotificationDate}
                    showMarkAsRead={true}
                    compact={false}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    py: 6,
                    px: 3,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <MailIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No notifications yet
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                    You're all caught up!
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          {recentNotifications.length > 0 && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                bgcolor: "background.paper",
                borderTop: `1px solid ${theme.palette.divider}`,
                borderRadius: "0 0 12px 12px",
              }}
            >
              <Button
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                  navigate("/notifications");
                }}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  borderRadius: "0 0 12px 12px",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </Paper>
      </Menu>
    </Box>
  );
};

export default NotificationAccordion;
